import {
  Button,
  Dropdown,
  DropdownSkeleton,
  IconButton,
  InlineLoading,
  InlineNotification,
  NumberInput,
  Select,
  SelectItem,
  Stack,
  TextInput,
  Tile,
} from '@carbon/react';
import { Add, Renew, TrashCan } from '@carbon/icons-react';
import { asProblem, PageHeader, problemMessage } from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { hoApi } from '../api';
import { ErrorNotice, TransientNotice } from '../common/Notices';
import {
  findProblems,
  generateDraft,
  newArea,
  renumber,
  setTableCount,
  toRequest,
  type DraftArea,
  type DraftTable,
  type Problem,
  type Shape,
  type Taken,
} from './draft';
import './tables.scss';

const SHAPES: Shape[] = ['square', 'rect', 'round'];
const NO_CODES: Taken = { areaCodes: new Set(), tableCodes: new Set() };

interface StoreOption {
  id: string;
  label: string;
}

async function loadStores(): Promise<StoreOption[]> {
  const { data, error } = await hoApi.GET('/api/v1/identity/stores', {
    params: { query: { pageSize: 500, sort: 'code' } },
  });
  if (!data) throw error;
  return data.items.map((s) => ({ id: s.id, label: `${s.code} · ${s.name}` }));
}

async function loadTaken(storeId: string): Promise<Taken & { areas: number }> {
  const { data, error } = await hoApi.GET('/api/v1/tables/stores/{storeId}/codes', { params: { path: { storeId } } });
  if (!data) throw error;
  return { areaCodes: new Set(data.areaCodes), tableCodes: new Set(data.tableCodes), areas: data.areaCodes.length };
}

/**
 * Sets up a store's tables quickly (FR-TBL-01): enter how many areas and tables, get a draft with names, codes and
 * seats filled in, adjust it, then save everything at once. Each area gets its own floor plan with the tables in rows.
 */
export function QuickSetupPage() {
  const { t } = useTranslation();
  const stores = useQuery({ queryKey: ['ho', 'tables', 'stores'], queryFn: loadStores });
  const [store, setStore] = useState<StoreOption>();
  const taken = useQuery({
    queryKey: ['ho', 'tables', 'codes', store?.id],
    queryFn: () => loadTaken(store?.id ?? ''),
    enabled: Boolean(store),
  });
  const [areas, setAreas] = useState(2);
  const [tablesPerArea, setTablesPerArea] = useState(10);
  const [capacity, setCapacity] = useState(4);
  const [draft, setDraft] = useState<DraftArea[]>();
  const [saved, setSaved] = useState<{ store: string; areas: number; tables: number }>();

  const codes = taken.data ?? NO_CODES;
  const problems = useMemo(() => (draft ? findProblems(draft, codes) : new Map<string, Problem>()), [draft, codes]);
  const tableTotal = draft?.reduce((n, a) => n + a.tables.length, 0) ?? 0;

  const save = useMutation({
    mutationFn: async () => {
      if (!store || !draft) return undefined;
      const { data, error } = await hoApi.POST('/api/v1/tables/stores/{storeId}/quick-setup', {
        params: { path: { storeId: store.id } },
        body: toRequest(draft),
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (result) => {
      if (!result || !store) return;
      setSaved({ store: store.label, areas: result.areas.length, tables: Number(result.tableCount) });
      setDraft(undefined);
      void taken.refetch();
    },
  });

  const generate = () => {
    setSaved(undefined);
    save.reset();
    setDraft(
      generateDraft({
        areas,
        tablesPerArea,
        capacity,
        shape: 'square',
        areaName: (n) => t('tableSetup.areaName', { n }),
        taken: codes,
        existingAreas: taken.data?.areas ?? 0,
      }),
    );
  };

  const updateArea = (key: string, change: Partial<DraftArea>) =>
    setDraft((d) => d?.map((a) => (a.key === key ? { ...a, ...change } : a)));
  const updateTable = (areaKey: string, tableKey: string, change: Partial<DraftTable>) =>
    setDraft((d) =>
      d?.map((a) =>
        a.key === areaKey
          ? { ...a, tables: a.tables.map((tb) => (tb.key === tableKey ? { ...tb, ...change } : tb)) }
          : a,
      ),
    );
  const problemText = (key: string) => {
    const problem = problems.get(key);
    return problem ? t(`tableSetup.problems.${problem}` as I18nKey) : undefined;
  };

  return (
    <section className="pos-admin-page pos-table-setup">
      <PageHeader title={t('tableSetup.title')} description={t('tableSetup.hint')} />

      {saved && (
        <div>
          <TransientNotice
            kind="success"
            title={t('tableSetup.saved', { areas: saved.areas, tables: saved.tables, store: saved.store })}
            onDone={() => setSaved(undefined)}
          />
          <Link to="/tables/areas">{t('tableSetup.viewAreas')}</Link>
        </div>
      )}
      {(stores.error || taken.error) && (
        <ErrorNotice
          title={problemMessage(t, asProblem(stores.error ?? taken.error))}
          onRetry={() => void (stores.error ? stores.refetch() : taken.refetch())}
        />
      )}

      <Tile>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            generate();
          }}
        >
          <Stack gap={6}>
            {stores.isLoading ? (
              <DropdownSkeleton />
            ) : (
              <Dropdown<StoreOption>
                id="table-setup-store"
                titleText={t('tableSetup.store')}
                label={t('tableSetup.chooseStore')}
                items={stores.data ?? []}
                itemToString={(s) => s?.label ?? ''}
                selectedItem={store}
                onChange={({ selectedItem }) => {
                  setStore(selectedItem ?? undefined);
                  setDraft(undefined);
                }}
              />
            )}
            {taken.data && taken.data.areas > 0 && (
              <p className="pos-table-setup__hint">
                {t('tableSetup.existing', { areas: taken.data.areas, tables: taken.data.tableCodes.size })}
              </p>
            )}
            <div className="pos-table-setup__numbers">
              <NumberInput
                id="table-setup-areas"
                label={t('tableSetup.areas')}
                min={1}
                max={50}
                value={areas}
                onChange={(_, { value }) => setAreas(Number(value))}
              />
              <NumberInput
                id="table-setup-tables"
                label={t('tableSetup.tablesPerArea')}
                min={1}
                max={200}
                value={tablesPerArea}
                onChange={(_, { value }) => setTablesPerArea(Number(value))}
              />
              <NumberInput
                id="table-setup-capacity"
                label={t('tableSetup.seats')}
                min={1}
                max={100}
                value={capacity}
                onChange={(_, { value }) => setCapacity(Number(value))}
              />
            </div>
            <div>
              <Button
                type="submit"
                kind={draft ? 'secondary' : 'primary'}
                disabled={!store || taken.isLoading || areas < 1 || tablesPerArea < 1}
              >
                {draft ? t('tableSetup.generateAgain') : t('tableSetup.generate')}
              </Button>
            </div>
          </Stack>
        </form>
      </Tile>

      {draft && (
        <>
          <h2 className="pos-table-setup__summary">
            {t('tableSetup.summary', { areas: draft.length, tables: tableTotal })}
          </h2>
          {draft.map((area) => (
            <Tile key={area.key} className="pos-table-setup__area" role="group" aria-label={area.name}>
              <div className="pos-table-setup__area-head">
                <TextInput
                  id={`${area.key}-name`}
                  labelText={t('tableSetup.areaNameLabel')}
                  required
                  value={area.name}
                  invalid={problems.has(`${area.key}:name`)}
                  invalidText={problemText(`${area.key}:name`)}
                  onChange={(e) => updateArea(area.key, { name: e.target.value })}
                />
                <TextInput
                  id={`${area.key}-code`}
                  labelText={t('tableSetup.areaCode')}
                  required
                  value={area.code}
                  invalid={problems.has(area.key)}
                  invalidText={problemText(area.key)}
                  onChange={(e) => updateArea(area.key, { code: e.target.value.toUpperCase() })}
                />
                <NumberInput
                  id={`${area.key}-count`}
                  label={t('tableSetup.tableCount')}
                  min={0}
                  max={200}
                  value={area.tables.length}
                  onChange={(_, { value }) => setDraft((d) => d && setTableCount(d, area.key, Number(value), codes))}
                />
                <div className="pos-table-setup__area-actions">
                  <Button
                    kind="ghost"
                    renderIcon={Renew}
                    onClick={() => setDraft((d) => d && renumber(d, area.key, codes))}
                  >
                    {t('tableSetup.renumber')}
                  </Button>
                  <IconButton
                    kind="ghost"
                    label={t('tableSetup.removeArea', { name: area.name })}
                    onClick={() => setDraft((d) => d?.filter((a) => a.key !== area.key))}
                  >
                    <TrashCan />
                  </IconButton>
                </div>
              </div>

              <ul className="pos-table-setup__tables" aria-label={t('tableSetup.tablesOf', { name: area.name })}>
                {area.tables.map((table) => (
                  <li key={table.key} className="pos-table-setup__table">
                    <TextInput
                      id={`${table.key}-code`}
                      labelText={t('tableSetup.tableCode')}
                      required
                      value={table.code}
                      invalid={problems.has(table.key) && problems.get(table.key) !== 'capacity'}
                      invalidText={problemText(table.key)}
                      onChange={(e) => updateTable(area.key, table.key, { code: e.target.value.toUpperCase() })}
                    />
                    <NumberInput
                      id={`${table.key}-seats`}
                      label={t('tableSetup.seatsShort')}
                      min={1}
                      max={100}
                      hideSteppers
                      value={table.capacity}
                      invalid={problems.get(table.key) === 'capacity'}
                      invalidText={problemText(table.key)}
                      onChange={(_, { value }) => updateTable(area.key, table.key, { capacity: Number(value) })}
                    />
                    <Select
                      id={`${table.key}-shape`}
                      labelText={t('tableSetup.shape')}
                      value={table.shape}
                      onChange={(e) => updateTable(area.key, table.key, { shape: e.target.value as Shape })}
                    >
                      {SHAPES.map((shape) => (
                        <SelectItem key={shape} value={shape} text={t(`tableSetup.shapes.${shape}`)} />
                      ))}
                    </Select>
                    <IconButton
                      kind="ghost"
                      label={t('tableSetup.removeTable', { code: table.code })}
                      onClick={() =>
                        setDraft((d) =>
                          d?.map((a) =>
                            a.key === area.key ? { ...a, tables: a.tables.filter((x) => x.key !== table.key) } : a,
                          ),
                        )
                      }
                    >
                      <TrashCan />
                    </IconButton>
                  </li>
                ))}
              </ul>
              <Button
                kind="tertiary"
                renderIcon={Add}
                onClick={() => setDraft((d) => d && setTableCount(d, area.key, area.tables.length + 1, codes))}
              >
                {t('tableSetup.addTable')}
              </Button>
            </Tile>
          ))}

          {save.error && <ErrorNotice title={problemMessage(t, asProblem(save.error))} />}
          {problems.size > 0 && (
            <InlineNotification
              kind="warning"
              lowContrast
              hideCloseButton
              title={t('tableSetup.fix', { count: problems.size })}
            />
          )}
          <div className="pos-table-setup__footer">
            <Button
              kind="tertiary"
              renderIcon={Add}
              onClick={() =>
                setDraft((d) => [
                  ...(d ?? []),
                  newArea(d ?? [], codes, {
                    name: t('tableSetup.areaName', { n: (taken.data?.areas ?? 0) + (d?.length ?? 0) + 1 }),
                    tables: tablesPerArea,
                    capacity,
                    shape: 'square',
                  }),
                ])
              }
            >
              {t('tableSetup.addArea')}
            </Button>
            <Button kind="secondary" onClick={() => setDraft(undefined)}>
              {t('tableSetup.discard')}
            </Button>
            <Button
              kind="primary"
              disabled={problems.size > 0 || draft.length === 0 || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? (
                <InlineLoading description={t('admin.saving')} />
              ) : (
                t('tableSetup.save', { areas: draft.length, tables: tableTotal })
              )}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
